import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  computed,
  effect,
  inject,
  untracked,
  viewChild,
} from '@angular/core';

import { ComponentAccess } from '../../../../common/decorators/component-access.decorator';
import { OnboardingService } from './onboarding.service';
import { TourService } from './tour.service';
import { TOURS } from './tours';

/** Button copy — shared so the template and its spec can't drift. */
export enum TourCta {
  Skip = 'Skip tour',
  Back = 'Back',
  Next = 'Next',
  Done = 'Got it',
}

/** Gap between the spotlight and the caption card. */
const CARD_GAP = 14;

/**
 * Renders the guided-tour spotlight + caption card for whatever
 * {@link TourService} has active. One instance, mounted in AppComponent above
 * `ion-menu` so it can spotlight menu items as well as page chrome.
 *
 * It also owns the hand-off from {@link OnboardingService.tourRequested}, which
 * the welcome carousel sets when the user picks "Show me around".
 */
@Component({
  selector: 'app-tour-overlay',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './tour-overlay.component.html',
  styleUrls: ['./tour-overlay.component.scss'],
})
@ComponentAccess('TourOverlayComponent')
export class TourOverlayComponent {
  protected readonly tour = inject(TourService);
  private readonly onboarding = inject(OnboardingService);
  private readonly nextBtn = viewChild<ElementRef<HTMLButtonElement>>('nextBtn');

  protected readonly Cta = TourCta;
  protected readonly spot = this.tour.rect;

  /**
   * Place the card clear of the spotlight: below a target in the top half,
   * above one in the bottom half. Anchoring to the *far* edge means we never
   * need to know the card's own height — the browser resolves the other edge.
   */
  protected readonly cardTop = computed(() => {
    const r = this.spot();
    if (!r) return null;
    return this.placeBelow(r) ? r.top + r.height + CARD_GAP : null;
  });

  protected readonly cardBottom = computed(() => {
    const r = this.spot();
    if (!r) return null;
    return this.placeBelow(r) ? null : window.innerHeight - r.top + CARD_GAP;
  });

  constructor() {
    // The welcome carousel (or the menu) asks for a tour; we own the steps.
    effect(() => {
      const requested = this.onboarding.tourRequested();
      if (!requested) return;
      untracked(() => {
        this.onboarding.tourRequested.set(null);
        // Explicit request — run it even if the flag says it's been seen.
        this.tour.start(requested, TOURS[requested] ?? []);
      });
    });

    // Move focus to the primary action as each step lands, so the tour is
    // followable by keyboard and announced by a screen reader.
    effect(() => {
      this.tour.index();
      if (!this.tour.active()) return;
      untracked(() => queueMicrotask(() => this.nextBtn()?.nativeElement.focus()));
    });
  }

  /** Targets move under a rotation / keyboard / window resize. */
  @HostListener('window:resize')
  @HostListener('window:orientationchange')
  protected onViewportChange(): void {
    this.tour.remeasure();
  }

  /** Escape ends the tour — the same contract as every other dismissible layer. */
  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (this.tour.active()) this.tour.skip();
  }

  private placeBelow(r: { top: number; height: number }): boolean {
    return r.top + r.height / 2 < window.innerHeight / 2;
  }
}
